import { Building, EdlSiteMapPageComponent, GenericErrorPageComponent, PageNotFoundComponent, SessionService, UnauthorizedComponent, UserService } from '@edl-development/edl-common';
import { Ng2StateDeclaration, Transition } from '@uirouter/angular';
import { AuthenticatedHeaderComponent } from './layout/authenticated/authenticated-header/authenticated-header.component';
import { AuthenticatedComponent } from './layout/authenticated/authenticated/authenticated.component';
import { EdlAuthenticatedNavigationComponent } from './layout/authenticated/edl-authenticated-navigation.component';
import { EdlPublicComponent } from './layout/public/edl-public.component';
import { AdministrationLazyLoadedStates } from './lazy-loaded-states/administration.lazy-loaded-states';
import { AssignmentsLazyLoadedStates } from './lazy-loaded-states/assignments.lazy-loaded-states';
import { AttachmentsLazyLoadedStates } from './lazy-loaded-states/attachments.lazy-loaded-states';
import { BuildingsLazyLoadedStates } from './lazy-loaded-states/buildings.lazy-loaded-states';
import { CustomFormBuilderLazyLoadedStates } from './lazy-loaded-states/custom-form-builder.lazy-loaded-states';
import { DashboardLazyLoadedStates } from './lazy-loaded-states/dashboard.lazy-loaded-states';
import { MessagesLazyLoadedStates } from './lazy-loaded-states/messages.lazy-loaded-states';
import { PermissionsLazyLoadedStates } from './lazy-loaded-states/permissions.lazy-loaded-states';
import { SupervisionManagementLazyLoadedStates } from './lazy-loaded-states/supervision-management.lazy-loaded-states';
import { UsersAgreementLazyLoadedStates } from './lazy-loaded-states/user-agreement.lazy-loaded-states';
import { UserSummaryLazyLoadedStates } from './lazy-loaded-states/user-summary.lazy-loaded-states';
import { UsersLazyLoadedStates } from './lazy-loaded-states/users.lazy-loaded-states';
import { ReportingLazyLoadedStates } from './lazy-loaded-states/reporting.lazy-loaded-states';
import { PresetManagementLazyLoadedStates } from './lazy-loaded-states/preset-management.lazy-loaded-states';
import { LocalStorageKeys } from './model/enum/local-storage-keys.enum';
import { PrintLazyLoadedStates } from './lazy-loaded-states/print.lazy-loaded-states';
import { CustomSupervisionLazyLoadedStates } from './lazy-loaded-states/custom-supervision.lazy-loaded-states';

export function onEnterAuthenticated(trans: Transition) {
    var sessionService: SessionService = trans.injector().get(SessionService);

    if (!sessionService.isAuthenticated() || !sessionService.GetSessionValue(LocalStorageKeys.HAS_LOADED_DATA)) {
        sessionService.redirectState = {
            name: trans.$to().name,
            params: trans.params()
        };

        const targetRoute = sessionService.isAuthenticated() ? 'public.loading' : 'public.login';

        return trans.router.stateService.target(targetRoute);
    }

    return undefined;
}

export function onEnterSwitchPortals(transition: Transition) {
    return transition.router.stateService.target('public.loading');
}

export function resolveSwitchPortals(transition: Transition, userService: UserService) {
    const { portalId } = transition.params();

    return userService.
        switchPortal(portalId)
        .toPromise();
}

export function onEnterDashboard(trans: Transition) {
    var sessionService: SessionService = trans.injector().get(SessionService);

    if (!sessionService.isAuthenticated()) {
        return trans.router.stateService.target('public.login');
    }
    else {
        return trans.router.stateService.target('authenticated.dashboard.view', { loggedInPortal: sessionService.PortalSettings?.URLSubdirectory } );
    }
}

export const EdlPaetepStates: Ng2StateDeclaration[] = [{
    name: 'public',
    abstract: true,
    data: {
        isPublic: true
    },
    views: {
        'base': {
            component: EdlPublicComponent
        }
    }
}, {
    name: 'public.page-not-found',
    url: '/404',
    views: {
        'content@public': {
            component: PageNotFoundComponent
        }
    }
}, {
    name: 'public.unauthorized',
    url: '/401',
    views: {
        'content@public': {
            component: UnauthorizedComponent
        }
    }
}, {
    name: 'public.generic-error',
    url: '/500',
    views: {
        'content@public': {
            component: GenericErrorPageComponent
        }
    }
},
{
    name: 'public.logout',
}, {
    name: 'authenticated',
    abstract: true,
    url: '/:loggedInPortal',
    params: {
        loggedInPortal: {
            raw: true,
            type: 'string'
        }
    },
    views: {
        'base': {
            component: AuthenticatedComponent
        },
        'header@authenticated': {
            component: AuthenticatedHeaderComponent
        },
        'navigation@authenticated': {
            component: EdlAuthenticatedNavigationComponent
        }
    },
    onEnter: onEnterAuthenticated
}, {
    name: 'authenticated.switch-portals',
    url: '/switch-portals/:portalId',
    onEnter: onEnterSwitchPortals,
    resolve: [{
        token: 'portalData',
        deps: [Transition, UserService],
        resolveFn: resolveSwitchPortals
    }]
}, {
    name: 'authenticated.site-map',
    url: '/site-map',
    views: {
        'content@authenticated': {
            component: EdlSiteMapPageComponent
        }
    }
},
{
  name: 'root',
  url: '/',
  onEnter: onEnterDashboard
},
...PrintLazyLoadedStates.generateLazyLoadedStates(),
...DashboardLazyLoadedStates.generateLazyLoadedStates(),
...SupervisionManagementLazyLoadedStates.generateLazyLoadedStates(),
...UsersAgreementLazyLoadedStates.generateLazyLoadedStates(),
...UsersLazyLoadedStates.generateLazyLoadedStates(),
...AdministrationLazyLoadedStates.generateLazyLoadedStates(),
...PermissionsLazyLoadedStates.generateLazyLoadedStates(),
...MessagesLazyLoadedStates.generateLazyLoadedStates(),
...UserSummaryLazyLoadedStates.generateLazyLoadedStates(),
...AssignmentsLazyLoadedStates.generateLazyLoadedStates(),
...AttachmentsLazyLoadedStates.generateLazyLoadedStates(),
...BuildingsLazyLoadedStates.generateLazyLoadedStates(),
...CustomFormBuilderLazyLoadedStates.generateLazyLoadedStates(),
...ReportingLazyLoadedStates.generateLazyLoadedStates(),
...PresetManagementLazyLoadedStates.generateLazyLoadedStates(),
...CustomSupervisionLazyLoadedStates.generateLazyLoadedStates()
];
